<template>
  <div class="invoicing-settings">
    <PageHeader
      :title="$t('invoices.settings.title')"
      :description="$t('invoices.settings.description')"
      size="compact"
    />

    <div v-if="loading" class="invoicing-settings__loading">
      <LoadingSpinner />
    </div>

    <form v-else-if="settings" class="invoicing-settings__form" @submit.prevent="handleSave">
      <!-- Invoice number format -->
      <Card
        :title="$t('invoices.settings.numbering')"
        :subtitle="$t('invoices.settings.numbering_description')"
      >
        <div class="invoicing-settings__row">
          <div class="invoicing-settings__field">
            <TextInput
              id="prefix"
              v-model="form.invoice_number_prefix"
              :label="$t('invoices.settings.invoice_number_prefix')"
              :placeholder="$t('invoices.settings.invoice_number_prefix_placeholder')"
            />
            <p class="invoicing-settings__help">
              {{ $t('invoices.settings.invoice_number_prefix_help') }}
            </p>
          </div>
          <div class="invoicing-settings__field">
            <TextInput
              id="padding"
              v-model.number="form.invoice_number_padding"
              type="number"
              :label="$t('invoices.settings.invoice_number_padding')"
            />
            <p class="invoicing-settings__help">
              {{ $t('invoices.settings.invoice_number_padding_help') }}
            </p>
          </div>
        </div>
        <div class="invoicing-settings__row invoicing-settings__row--single">
          <ToggleInput
            id="include-year"
            v-model="form.invoice_number_include_year"
            :label="$t('invoices.settings.invoice_number_include_year')"
            :description="$t('invoices.settings.invoice_number_include_year_help')"
          />
        </div>

        <div class="invoicing-settings__preview">
          <strong>{{ $t('invoices.settings.numbering_preview') }}</strong>
          <span class="invoicing-settings__preview-number">{{ previewInvoiceNumber }}</span>
        </div>
      </Card>

      <!-- Defaults -->
      <Card :title="$t('invoices.settings.defaults')">
        <div class="invoicing-settings__row">
          <TextInput
            id="payment-term"
            v-model.number="form.default_payment_term_days"
            type="number"
            :label="$t('invoices.settings.payment_term_days')"
          />
          <TextInput
            id="vat-rate"
            v-model.number="form.default_vat_rate"
            type="number"
            :label="$t('invoices.settings.default_vat_rate')"
          />
        </div>
        <div class="invoicing-settings__row">
          <SelectInput
            id="currency"
            v-model="form.default_currency"
            :options="currencyOptions"
            :label="$t('invoices.settings.default_currency')"
          />
          <ToggleInput
            id="prices-incl-vat"
            v-model="form.prices_include_vat_default"
            :label="$t('invoices.settings.prices_include_vat_default')"
          />
        </div>
      </Card>

      <!-- Deposit -->
      <Card :title="$t('invoices.settings.deposit')">
        <div class="invoicing-settings__row invoicing-settings__row--single">
          <ToggleInput
            id="deposit-enabled"
            v-model="form.deposit_enabled"
            :label="$t('invoices.settings.deposit_enabled')"
          />
        </div>

        <template v-if="form.deposit_enabled">
          <div class="invoicing-settings__row">
            <SelectInput
              id="deposit-trigger"
              v-model="form.deposit_trigger_moment"
              :options="depositTriggerOptions"
              :label="$t('invoices.settings.deposit_trigger_moment')"
            />
            <TextInput
              id="deposit-percentage"
              v-model.number="form.deposit_percentage"
              type="number"
              :label="$t('invoices.settings.deposit_percentage')"
            />
          </div>

          <h4 class="invoicing-settings__section-title">
            {{ $t('invoices.settings.deposit_tiers') }}
          </h4>
          <p class="invoicing-settings__section-help">
            {{ $t('invoices.settings.deposit_tiers_help') }}
          </p>
          <div
            v-for="(tier, index) in form.deposit_tiers"
            :key="index"
            class="invoicing-settings__tier"
          >
            <TextInput
              :id="`deposit-tier-min-${index}`"
              v-model.number="tier.min_amount"
              type="number"
              :label="$t('invoices.settings.deposit_tier_min_amount')"
            />
            <TextInput
              :id="`deposit-tier-percentage-${index}`"
              v-model.number="tier.percentage"
              type="number"
              :label="$t('invoices.settings.deposit_tier_percentage')"
            />
            <button
              type="button"
              class="invoicing-settings__tier-remove"
              :title="$t('invoices.settings.deposit_tier_remove')"
              @click="removeDepositTier(index)"
            >
              <BaseIcon name="xmark" :size="14" />
            </button>
          </div>
          <Btn
            type="button"
            variant="dashed"
            size="sm"
            icon="plus"
            class="invoicing-settings__tier-add"
            :text="$t('invoices.settings.deposit_tier_add')"
            @click="addDepositTier"
          />

          <div class="invoicing-settings__row">
            <TextInput
              id="deposit-fixed"
              v-model.number="form.deposit_fixed_amount"
              type="number"
              :label="$t('invoices.settings.deposit_fixed_amount')"
            />
            <ToggleInput
              id="deposit-auto-send"
              v-model="form.deposit_auto_send"
              :label="$t('invoices.settings.deposit_auto_send')"
            />
          </div>

          <div class="invoicing-settings__divider"></div>

          <h4 class="invoicing-settings__section-title">
            {{ $t('invoices.settings.deposit_layout') }}
          </h4>
          <p class="invoicing-settings__section-help">
            {{ $t('invoices.settings.deposit_layout_help') }}
          </p>
          <RadioCardGroup
            v-model="form.deposit_invoice_layout"
            :options="depositLayoutOptions"
            layout="grid"
            class="invoicing-settings__layout"
          />

          <div class="invoicing-settings__row invoicing-settings__row--single">
            <div class="invoicing-settings__field">
              <TextInput
                id="deposit-description-template"
                v-model="form.deposit_description_template"
                :label="$t('invoices.settings.deposit_description_template')"
                :placeholder="$t('invoices.settings.deposit_description_template_placeholder')"
              />
              <p class="invoicing-settings__help">
                {{ $t('invoices.settings.deposit_description_template_help') }}
              </p>
            </div>
          </div>

          <div class="invoicing-settings__preview">
            <strong>{{ $t('invoices.settings.deposit_description_preview') }}</strong>
            <span class="invoicing-settings__preview-text">{{ depositTemplatePreview }}</span>
          </div>
        </template>
      </Card>

      <!-- Final invoice trigger -->
      <Card :title="$t('invoices.settings.final_invoice')">
        <div class="invoicing-settings__row">
          <SelectInput
            id="final-trigger"
            v-model="form.final_invoice_trigger"
            :options="finalTriggerOptions"
            :label="$t('invoices.settings.final_invoice_trigger')"
          />
          <ToggleInput
            id="final-auto-send"
            v-model="form.final_invoice_auto_send"
            :label="$t('invoices.settings.final_invoice_auto_send')"
          />
        </div>
        <p
          v-if="
            !hasSlimWerken &&
            form.final_invoice_trigger !== 'manual_only' &&
            form.final_invoice_trigger !== 'disabled'
          "
          class="invoicing-settings__notice"
        >
          <BaseIcon name="info" size="14" />
          {{ $t('invoices.settings.requires_slim_werken') }}
        </p>
      </Card>

      <Card :title="$t('invoices.settings.definitive_deviation')">
        <p class="invoicing-settings__help invoicing-settings__help--block">
          {{ $t('invoices.settings.price_deviation_moved') }}
        </p>
        <div class="invoicing-settings__row">
          <TextInput
            id="warn-deviation"
            v-model.number="form.warn_on_definitive_deviation"
            type="number"
            :label="$t('invoices.settings.warn_on_definitive_deviation')"
          />
        </div>
      </Card>

      <Card :title="$t('invoices.settings.public_payment')">
        <div class="invoicing-settings__row invoicing-settings__row--single">
          <ToggleInput
            id="public-payment"
            v-model="form.public_payment_enabled"
            :label="$t('invoices.settings.public_payment_enabled')"
            :disabled="!hasSlimInnen"
          />
        </div>
      </Card>

      <Card :title="$t('invoices.settings.merging')">
        <div class="invoicing-settings__row invoicing-settings__row--single">
          <ToggleInput
            id="allow-merging"
            v-model="form.allow_invoice_merging"
            :label="$t('invoices.settings.allow_invoice_merging')"
            :description="$t('invoices.settings.allow_invoice_merging_description')"
          />
        </div>
      </Card>

      <!-- Invoice email: default attachments + BCC -->
      <Card
        :title="$t('invoices.settings.default_attachments')"
        :subtitle="$t('invoices.settings.default_attachments_help')"
      >
        <ul v-if="defaultAttachments.length" class="invoicing-settings__attachments">
          <li
            v-for="(att, index) in defaultAttachments"
            :key="att.id || att.url"
            class="invoicing-settings__attachment"
          >
            <BaseIcon name="file" :size="14" />
            <a
              :href="att.url"
              target="_blank"
              rel="noopener"
              class="invoicing-settings__attachment-name"
            >
              {{ att.name }}
            </a>
            <button
              type="button"
              class="invoicing-settings__attachment-remove"
              :title="$t('invoices.settings.remove_attachment')"
              @click="removeDefaultAttachment(index)"
            >
              <BaseIcon name="xmark" :size="14" />
            </button>
          </li>
        </ul>
        <input
          ref="attachmentInputRef"
          type="file"
          accept="application/pdf,.pdf"
          multiple
          class="invoicing-settings__attachment-input"
          @change="handleAttachmentUpload"
        />
        <Btn
          :text="$t('invoices.settings.add_attachment')"
          variant="dashed"
          icon="plus"
          :loading="isAttachmentUploading"
          @click="triggerAttachmentUpload"
        />
      </Card>

      <Card :title="$t('invoices.settings.bcc')" :subtitle="$t('invoices.settings.bcc_help')">
        <div class="invoicing-settings__row invoicing-settings__row--single">
          <TagInput
            id="invoice-bcc-emails"
            v-model="form.invoice_bcc_emails"
            type="email"
            :label="$t('invoices.settings.bcc_emails')"
            :placeholder="$t('invoices.settings.bcc_emails_placeholder')"
          />
        </div>
      </Card>

      <!-- Payment instructions / footer text -->
      <Card :title="$t('invoices.settings.template')">
        <div class="invoicing-settings__row invoicing-settings__row--single">
          <div class="invoicing-settings__textarea">
            <label for="payment-instructions">
              {{ $t('invoices.settings.payment_instructions') }}
            </label>
            <textarea
              id="payment-instructions"
              v-model="form.payment_instructions"
              rows="3"
              :placeholder="$t('invoices.settings.payment_instructions_placeholder')"
            ></textarea>
          </div>
        </div>
        <div class="invoicing-settings__row invoicing-settings__row--single">
          <div class="invoicing-settings__textarea">
            <label for="footer-text">{{ $t('invoices.settings.footer_text') }}</label>
            <textarea
              id="footer-text"
              v-model="form.footer_text"
              rows="2"
              :placeholder="$t('invoices.settings.footer_text_placeholder')"
            ></textarea>
          </div>
        </div>
      </Card>

      <div class="invoicing-settings__actions">
        <Btn variant="secondary" type="submit" :loading="saving">
          {{ $t('common.save') }}
        </Btn>
      </div>
    </form>
  </div>
</template>

<script>
  import { defineComponent, ref, onMounted, getCurrentInstance, computed } from 'vue';
  import { useInvoiceStore } from '@/store/invoiceStore';
  import { useToastStore } from '@/store/toastStore';
  import { useUserStore } from '@/store/userStore';
  import { useModules } from '@/composables/useModules';
  import FileService from '@/services/fileService';

  import PageHeader from '@/components/partials/PageHeader.vue';
  import Card from '@/components/partials/Card.vue';
  import Btn from '@/components/partials/Btn.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import RadioCardGroup from '@/components/inputs/RadioCardGroup.vue';
  import TagInput from '@/components/inputs/TagInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default defineComponent({
    name: 'SettingsInvoicingView',

    components: {
      PageHeader,
      Card,
      Btn,
      LoadingSpinner,
      TextInput,
      SelectInput,
      ToggleInput,
      RadioCardGroup,
      TagInput,
      BaseIcon,
    },

    setup() {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const invoiceStore = useInvoiceStore();
      const toastStore = useToastStore();
      const userStore = useUserStore();
      const { hasSlimWerken, hasSlimInnen } = useModules();

      const loading = ref(true);
      const saving = ref(false);
      const settings = computed(() => invoiceStore.settings);

      const form = ref({});

      const currencyOptions = [
        { value: 'EUR', label: 'EUR (€)' },
        { value: 'USD', label: 'USD ($)' },
        { value: 'GBP', label: 'GBP (£)' },
      ];

      const depositTriggerOptions = [
        {
          value: 'proforma_accepted',
          label: $t('invoices.settings.deposit_trigger.proforma_accepted'),
        },
        {
          value: 'definitive_accepted',
          label: $t('invoices.settings.deposit_trigger.definitive_accepted'),
        },
      ];

      const previewInvoiceNumber = computed(() => {
        const prefix = (form.value.invoice_number_prefix || '').trim();
        const padding = Number(form.value.invoice_number_padding) || 4;
        const includeYear = form.value.invoice_number_include_year !== false;
        const year = new Date().getFullYear();
        const seq = String(1).padStart(Math.max(1, Math.min(10, padding)), '0');
        if (prefix && includeYear) return `${prefix}-${year}-${seq}`;
        if (prefix) return `${prefix}-${seq}`;
        if (includeYear) return `${year}-${seq}`;
        return seq;
      });

      const depositLayoutOptions = computed(() => [
        {
          value: 'itemized',
          icon: 'list-ul',
          title: $t('invoices.settings.deposit_layout_itemized_title'),
          description: $t('invoices.settings.deposit_layout_itemized_description'),
          badge: $t('invoices.settings.deposit_layout_recommended'),
        },
        {
          value: 'detailed',
          icon: 'notes',
          title: $t('invoices.settings.deposit_layout_detailed_title'),
          description: $t('invoices.settings.deposit_layout_detailed_description'),
        },
        {
          value: 'per_vat_rate',
          icon: 'columns',
          title: $t('invoices.settings.deposit_layout_per_vat_rate_title'),
          description: $t('invoices.settings.deposit_layout_per_vat_rate_description'),
        },
        {
          value: 'simple',
          icon: 'text',
          title: $t('invoices.settings.deposit_layout_simple_title'),
          description: $t('invoices.settings.deposit_layout_simple_description'),
        },
      ]);

      const addDepositTier = () => {
        form.value.deposit_tiers.push({ min_amount: 5000, percentage: 50 });
      };

      const removeDepositTier = (index) => {
        form.value.deposit_tiers.splice(index, 1);
      };

      const depositTemplatePreview = computed(() => {
        const fallback = 'Aanbetaling {percentage}% conform offerte {proposal_number}';
        const template = (form.value.deposit_description_template || '').trim() || fallback;
        const percentage = Number(form.value.deposit_percentage) || 30;
        return template
          .replaceAll('{percentage}', String(percentage))
          .replaceAll('{proposal_number}', 'SOL-045')
          .replaceAll('{project_number}', 'PRJ-2026-045')
          .replaceAll('{vat_rate}', '21');
      });

      const finalTriggerOptions = computed(() => [
        {
          value: 'disabled',
          label: $t('invoices.settings.final_trigger.disabled'),
        },
        {
          value: 'manual_only',
          label: $t('invoices.settings.final_trigger.manual_only'),
        },
        {
          value: 'project_completed',
          label: $t('invoices.settings.final_trigger.project_completed'),
        },
        {
          value: 'work_order_completed',
          label: $t('invoices.settings.final_trigger.work_order_completed'),
        },
      ]);

      const initForm = (data) => {
        if (!data) return;
        form.value = {
          invoice_number_prefix: data.invoice_number_prefix || 'F',
          invoice_number_include_year: data.invoice_number_include_year ?? true,
          invoice_number_padding: data.invoice_number_padding ?? 4,
          default_payment_term_days: data.default_payment_term_days ?? 14,
          default_vat_rate: data.default_vat_rate ?? 21,
          default_currency: data.default_currency || 'EUR',
          prices_include_vat_default: data.prices_include_vat_default ?? true,
          deposit_enabled: data.deposit_enabled ?? false,
          deposit_trigger_moment: data.deposit_trigger_moment || 'definitive_accepted',
          deposit_percentage: data.deposit_percentage ?? 30,
          deposit_tiers: Array.isArray(data.deposit_tiers)
            ? data.deposit_tiers.map((tier) => ({ ...tier }))
            : [],
          deposit_fixed_amount: data.deposit_fixed_amount ?? null,
          deposit_auto_send: data.deposit_auto_send ?? false,
          deposit_invoice_layout: data.deposit_invoice_layout || 'detailed',
          deposit_description_template: data.deposit_description_template || '',
          final_invoice_trigger: data.final_invoice_trigger || 'manual_only',
          final_invoice_auto_send: data.final_invoice_auto_send ?? false,
          commercial_cap_enabled: data.commercial_cap_enabled ?? false,
          commercial_cap_max_difference: data.commercial_cap_max_difference ?? 3,
          warn_on_definitive_deviation: data.warn_on_definitive_deviation ?? 10,
          public_payment_enabled: data.public_payment_enabled ?? false,
          allow_invoice_merging: data.allow_invoice_merging ?? true,
          payment_instructions: data.payment_instructions || '',
          footer_text: data.footer_text || '',
          invoice_bcc_emails: Array.isArray(data.invoice_bcc_emails)
            ? data.invoice_bcc_emails.join(', ')
            : '',
        };
        defaultAttachments.value = Array.isArray(data.default_invoice_attachments)
          ? [...data.default_invoice_attachments]
          : [];
      };

      // --- Default invoice attachments ---
      // Saved immediately (not via the save button), same as default proposal attachments.
      const defaultAttachments = ref([]);
      const attachmentInputRef = ref(null);
      const isAttachmentUploading = ref(false);
      const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;

      const saveDefaultAttachments = () =>
        invoiceStore.updateSettings({ default_invoice_attachments: defaultAttachments.value });

      const triggerAttachmentUpload = () => {
        attachmentInputRef.value?.click();
      };

      const handleAttachmentUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (!files.length) return;

        isAttachmentUploading.value = true;
        let uploadedCount = 0;
        try {
          for (const file of files) {
            const isPdf =
              file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
            if (!isPdf) {
              toastStore.addToast({
                type: 'error',
                message: $t('invoices.settings.attachment_only_pdf'),
              });
              continue;
            }
            if (file.size > MAX_ATTACHMENT_SIZE) {
              toastStore.addToast({
                type: 'error',
                message: $t('invoices.settings.attachment_too_large', { name: file.name }),
              });
              continue;
            }

            const uploaded = await FileService.uploadFile(file, {
              type: 'invoice_attachment',
              organizationId: userStore.currentOrganization?.id,
              context: 'organization',
            });

            defaultAttachments.value.push({
              id: uploaded.id,
              name: uploaded.original_name || file.name,
              url: uploaded.url,
              file_name: uploaded.file_name,
              size: uploaded.size ?? file.size,
            });
            uploadedCount += 1;
          }

          if (uploadedCount > 0) {
            await saveDefaultAttachments();
            toastStore.addToast({
              type: 'success',
              message: $t('invoices.settings.attachment_added'),
            });
          }
        } catch (error) {
          console.error('Failed to upload default invoice attachment', error);
          toastStore.addToast({
            type: 'error',
            message: $t('invoices.settings.attachment_save_error'),
          });
        } finally {
          isAttachmentUploading.value = false;
        }
      };

      const removeDefaultAttachment = async (index) => {
        const removed = defaultAttachments.value.splice(index, 1);
        try {
          await saveDefaultAttachments();
          toastStore.addToast({
            type: 'success',
            message: $t('invoices.settings.attachment_removed'),
          });
        } catch (error) {
          console.error('Failed to remove default invoice attachment', error);
          defaultAttachments.value.splice(index, 0, ...removed);
          toastStore.addToast({
            type: 'error',
            message: $t('invoices.settings.attachment_save_error'),
          });
        }
      };

      const load = async () => {
        loading.value = true;
        try {
          await invoiceStore.fetchSettings(true);
          initForm(settings.value);
        } catch (error) {
          console.error('Failed to load invoice settings', error);
          toastStore.addToast({
            type: 'error',
            message: $t('invoices.settings.load_error'),
          });
        } finally {
          loading.value = false;
        }
      };

      const handleSave = async () => {
        saving.value = true;
        try {
          await invoiceStore.updateSettings({
            ...form.value,
            invoice_bcc_emails: String(form.value.invoice_bcc_emails || '')
              .split(/[,;]/)
              .map((email) => email.trim())
              .filter(Boolean),
          });
          toastStore.addToast({
            type: 'success',
            message: $t('invoices.settings.saved'),
          });
        } catch (error) {
          const msg = error?.response?.data?.error || $t('invoices.settings.save_error');
          toastStore.addToast({ type: 'error', message: msg });
        } finally {
          saving.value = false;
        }
      };

      onMounted(() => {
        load();
        invoiceStore.fetchTriggerAvailability().catch(() => null);
      });

      return {
        loading,
        saving,
        settings,
        form,
        currencyOptions,
        depositTriggerOptions,
        finalTriggerOptions,
        depositLayoutOptions,
        depositTemplatePreview,
        addDepositTier,
        removeDepositTier,
        previewInvoiceNumber,
        hasSlimWerken,
        hasSlimInnen,
        handleSave,
        defaultAttachments,
        attachmentInputRef,
        isAttachmentUploading,
        triggerAttachmentUpload,
        handleAttachmentUpload,
        removeDefaultAttachment,
        $t,
      };
    },
  });
</script>

<style scoped lang="scss">
  .invoicing-settings {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    &__loading {
      padding: 2rem;
      display: flex;
      justify-content: center;
    }

    &__form {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
    }

    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      margin-bottom: 0.875rem;

      &:last-child {
        margin-bottom: 0;
      }

      &--single {
        grid-template-columns: 1fr;
      }

      @media (max-width: 640px) {
        grid-template-columns: 1fr;
      }
    }

    &__field {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    &__help {
      font-size: 0.75rem;
      color: var(--slate-500);
      margin: 0;
      line-height: 1.4;

      &--block {
        margin-bottom: 0.875rem;
      }
    }

    &__divider {
      height: 1px;
      background: var(--slate-200);
      margin: 1rem 0;
    }

    &__section-title {
      margin: 0 0 0.25rem;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    &__section-help {
      margin: 0 0 0.75rem;
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.4;
    }

    &__layout {
      margin-bottom: 1rem;
    }

    &__tier {
      display: grid;
      grid-template-columns: 1fr 1fr auto;
      align-items: end;
      gap: 1rem;
      margin-bottom: 0.75rem;
    }

    &__tier-remove {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border: none;
      background: none;
      border-radius: 0.375rem;
      cursor: pointer;
      color: var(--slate-400);

      @include hover {
        background-color: var(--slate-100);
        color: var(--slate-600);
      }
    }

    &__tier-add {
      margin-bottom: 1rem;
    }

    &__preview-text {
      font-family: 'SF Mono', 'Fira Code', Menlo, Consolas, monospace;
      font-size: 0.8125rem;
      color: var(--brand-700);
      font-weight: 500;
    }

    &__textarea {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;

      label {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--slate-700);
      }

      textarea {
        width: 100%;
        padding: 0.625rem 0.75rem;
        border: 1px solid var(--slate-200);
        border-radius: 0.5rem;
        font-size: 0.875rem;
        resize: vertical;
        font-family: inherit;

        &:focus {
          outline: none;
          border-color: var(--brand-400);
          box-shadow: 0 0 0 3px var(--brand-100);
        }
      }
    }

    &__notice {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.75rem;
      color: var(--amber-700);
      background: var(--amber-50);
      padding: 0.5rem 0.75rem;
      border-radius: 0.375rem;
      margin: 0.5rem 0 0;
    }

    &__preview {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      margin-top: 0.5rem;
      padding: 0.75rem 1rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;

      strong {
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--slate-600);
      }
    }

    &__preview-number {
      font-family: 'SF Mono', 'Fira Code', Menlo, Consolas, monospace;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--brand-700);
    }

    &__attachments {
      list-style: none;
      margin: 0 0 0.75rem;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      max-width: 480px;
    }

    &__attachment {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: var(--slate-50);
      color: var(--slate-700);
      font-size: 0.875rem;
    }

    &__attachment-name {
      flex: 1;
      color: var(--slate-700);
      text-decoration: none;
      @include ellipsis(1);

      @include hover {
        text-decoration: underline;
      }
    }

    &__attachment-remove {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border: none;
      background: none;
      border-radius: 4px;
      cursor: pointer;
      color: var(--slate-400);

      @include hover {
        background-color: var(--slate-200);
        color: var(--slate-600);
      }
    }

    &__attachment-input {
      display: none;
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      padding-top: 1rem;
      border-top: 1px solid var(--slate-200);
    }
  }
</style>
